import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router";
import {
    Bars3Icon,
    XMarkIcon,
    QrCodeIcon,
    ExclamationTriangleIcon,
    UserGroupIcon,
    ArrowRightOnRectangleIcon,
    ChevronLeftIcon,
    MapPinIcon,
    ShieldCheckIcon,
} from "@heroicons/react/24/outline";
import sunnyHomesCrmLogo from "assets/images/SH-logo.png";

/**
 * WhsSideMenu
 * 
 * Full-height left-side navigation sidebar for WHS portal:
 * - On web view (desktop/large screens >= 1024px): Full-height docked sidebar, open by default
 * - On mobile view & small screens (< 1024px): Collapsed by default, opens as a full-height slide-over drawer
 * 
 * Navigation tools:
 * - Job Attendance
 * - Report Incident
 * - Toolbox Talk
 */
export default function WhsSideMenu({
    token,
    jobData,
    userForm,
    workerStatus,
    activeCheckInDate,
    onCheckOutClick,
    currentPage = "attendance", // "attendance" | "report-incident" | "toolbox-talk"
    companyLogo = sunnyHomesCrmLogo,
    className = "",
    isOpen: controlledIsOpen,
    onToggle: controlledOnToggle,
}) {
    // Open by default on desktop/web view (>= 1024px), closed on mobile (< 1024px)
    const [internalOpen, setInternalOpen] = useState(() => {
        if (typeof window !== "undefined") {
            const stored = localStorage.getItem("whs_sidebar_open");
            if (stored !== null) return stored === "true";
            return window.innerWidth >= 1024;
        }
        return false;
    });

    const isControlled = controlledIsOpen !== undefined;
    const isOpen = isControlled ? controlledIsOpen : internalOpen;

    const setIsOpen = (nextVal) => {
        if (isControlled && controlledOnToggle) {
            controlledOnToggle(nextVal);
        } else {
            setInternalOpen((prev) => {
                const resolved = typeof nextVal === "function" ? nextVal(prev) : nextVal;
                try {
                    localStorage.setItem("whs_sidebar_open", resolved ? "true" : "false");
                } catch (e) {
                    console.warn(e);
                }
                return resolved;
            });
        }
    };

    const menuRef = useRef(null);
    const navigate = useNavigate();

    // Auto-adjust state on window resize (close on mobile)
    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth < 1024) {
                setIsOpen(false);
            }
        };

        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    // On mobile screens (< 1024px), close when clicking outside or pressing Escape
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (window.innerWidth < 1024 && menuRef.current && !menuRef.current.contains(e.target)) {
                setIsOpen(false);
            }
        };

        const handleKeyDown = (e) => {
            if (e.key === "Escape" && isOpen) {
                if (window.innerWidth < 1024) {
                    setIsOpen(false);
                }
            }
        };

        if (isOpen) {
            document.addEventListener("mousedown", handleClickOutside);
            document.addEventListener("keydown", handleKeyDown);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, [isOpen]);

    const activeJobId = jobData?.jobId || jobData?.JobId || jobData?.displayJobId || "";
    const activeJobTitle = jobData?.jobTitle || (activeJobId ? `Job #${activeJobId}` : "Job");
    const activeAddress = [jobData?.streetAddress, jobData?.suburb, jobData?.city]
        .filter(Boolean)
        .join(", ");
    const isCheckedIn = Boolean(activeCheckInDate || workerStatus?.isCheckedIn || workerStatus?.IsCheckedIn);

    const handleNavigate = (destination) => {
        // On mobile screens, close the sidebar when an option is selected
        if (window.innerWidth < 1024) {
            setIsOpen(false);
        }
        if (currentPage === destination) return;

        const currentUserId =
            localStorage.getItem("whs_user_id") ||
            userForm?.userId ||
            workerStatus?.userId ||
            workerStatus?.UserId ||
            workerStatus?.data?.userId ||
            workerStatus?.data?.UserId ||
            null;

        const currentCompanyId =
            jobData?.companyId ||
            jobData?.CompanyId ||
            localStorage.getItem("whs_company_id") ||
            null;

        try {
            localStorage.setItem("whs_is_internal_user", "true");
        } catch (e) {
            console.warn("Storage warning:", e);
        }

        const navState = {
            jobData,
            companyId: currentCompanyId,
            token,
            userId: currentUserId,
            userForm,
            isInternalUser: true,
            activeCheckInDate: activeCheckInDate
                ? activeCheckInDate instanceof Date
                    ? activeCheckInDate.toISOString()
                    : activeCheckInDate
                : null,
            isCheckedIn: Boolean(activeCheckInDate || workerStatus?.isCheckedIn || workerStatus?.IsCheckedIn),
            workerStatus: workerStatus,
            fromMenu: true,
        };

        const effectiveToken = token || (jobData?.token || jobData?.Token || "");

        if (destination === "attendance") {
            navigate(effectiveToken ? `/whs/${effectiveToken}` : "/whs", { state: navState });
        } else if (destination === "report-incident") {
            navigate(effectiveToken ? `/whs/report-incident/${effectiveToken}` : "/whs/report-incident", { state: navState });
        } else if (destination === "toolbox-talk") {
            navigate(effectiveToken ? `/whs/toolbox-talk/${effectiveToken}` : "/whs/toolbox-talk", { state: navState });
        }
    };

    const navItems = [
        {
            id: "attendance",
            title: "Job Attendance",
            subtitle: "Check-In & Time Tracker",
            icon: QrCodeIcon,
            iconBg: "bg-emerald-50 text-emerald-600 border-emerald-200",
            activeBg: "bg-emerald-50/80 border-emerald-300 text-emerald-950 font-bold shadow-2xs",
        },
        {
            id: "report-incident",
            title: "Report Incident",
            subtitle: "Log safety incident or hazard",
            icon: ExclamationTriangleIcon,
            iconBg: "bg-rose-50 text-rose-600 border-rose-200",
            activeBg: "bg-rose-50/80 border-rose-300 text-rose-950 font-bold shadow-2xs",
        },
        {
            id: "toolbox-talk",
            title: "Toolbox Talk",
            subtitle: "Safety briefings & team talks",
            icon: UserGroupIcon,
            iconBg: "bg-amber-50 text-amber-600 border-amber-200",
            activeBg: "bg-amber-50/80 border-amber-300 text-amber-950 font-bold shadow-2xs",
        },
    ];

    return (
        <div ref={menuRef} className={className}>
            {/* Floating Re-Open / Menu Trigger (Visible on mobile, or when collapsed on desktop) */}
            {!isOpen && (
                <div
                    className="fixed z-40 transition-all duration-200"
                    style={{
                        top: "max(1.25rem, calc(env(safe-area-inset-top, 0px) + 0.875rem))",
                        left: "max(0.75rem, calc(env(safe-area-inset-left, 0px) + 0.75rem))",
                    }}
                >
                    <button
                        type="button"
                        onClick={() => setIsOpen(true)}
                        className="inline-flex items-center gap-2 px-3.5 py-2 sm:px-4 sm:py-2 rounded-2xl border shadow-sm hover:shadow-md transition-all cursor-pointer backdrop-blur-md text-xs font-bold active:scale-95 bg-white/95 hover:bg-white text-slate-700 hover:text-slate-900 border-slate-200/90 select-none touch-manipulation"
                        title="Open WHS Menu"
                        aria-label="Open WHS Menu"
                    >
                        <div className="relative flex items-center justify-center">
                            <Bars3Icon className="w-4 h-4 text-slate-700" />
                        </div>
                        <span>Menu</span>
                    </button>
                </div>
            )}

            {/* Mobile Backdrop Overlay (< lg) */}
            {isOpen && (
                <div
                    className="fixed inset-0 bg-slate-950/60 backdrop-blur-xs z-40 lg:hidden transition-opacity duration-300 animate-in fade-in"
                    onClick={() => setIsOpen(false)}
                    aria-hidden="true"
                />
            )}

            {/* Full-Height Left Sidebar */}
            <aside
                className={`fixed inset-y-0 left-0 z-50 lg:z-30 w-80 sm:w-80 lg:w-[350px] max-w-[88vw] h-full max-h-[100dvh] bg-white/95 backdrop-blur-xl border-r border-slate-200/80 shadow-xl lg:shadow-md flex flex-col justify-between font-sans transition-transform duration-300 ease-in-out ${isOpen ? "translate-x-0" : "-translate-x-full"
                    }`}
            >
                {/* Top Section: Header, JOb Info & Navigation */}
                <div className="flex flex-col min-h-0 overflow-y-auto">
                    {/* Sidebar Brand Header */}
                    <div
                        className="p-4 sm:p-4.5 border-b border-slate-100 bg-slate-50/70 flex items-center justify-between"
                        style={{
                            paddingTop: "max(1rem, calc(env(safe-area-inset-top, 0px) + 0.875rem))",
                        }}
                    >
                        <div className="flex items-center gap-2.5">
                            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary-600 text-white shadow-sm shadow-primary-500/20">
                                <ShieldCheckIcon className="w-5 h-5 text-white" />
                            </div>
                            <div>
                                <h2 className="text-xs font-bold text-slate-900 leading-tight">
                                    Sunny Homes WHS
                                </h2>
                                <p className="text-[10px] font-semibold text-slate-500">
                                    Job Portal
                                </p>
                            </div>
                        </div>

                        {/* Collapse / Close Button */}
                        <button
                            type="button"
                            onClick={() => setIsOpen(false)}
                            className="h-8 w-8 flex items-center justify-center rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-200/60 transition-colors cursor-pointer touch-manipulation"
                            title="Collapse sidebar"
                            aria-label="Collapse sidebar"
                        >
                            <XMarkIcon className="w-5 h-5 lg:hidden" />
                            <ChevronLeftIcon className="w-4 h-4 hidden lg:block" />
                        </button>
                    </div>




                    {/* Navigation Items */}
                    <div className="p-3 space-y-1.5">


                        {navItems.map((item) => {
                            const Icon = item.icon;
                            const isActive = currentPage === item.id;

                            return (
                                <button
                                    key={item.id}
                                    type="button"
                                    onClick={() => handleNavigate(item.id)}
                                    className={`w-full text-left p-2.5 rounded-xl border transition-all flex items-center justify-between gap-2.5 cursor-pointer group ${isActive
                                        ? `${item.activeBg} ring-1 ring-primary-500/10`
                                        : "bg-white hover:bg-slate-50 border-slate-200/70 text-slate-700"
                                        }`}
                                >
                                    <div className="flex items-center gap-2.5 min-w-0">
                                        <div
                                            className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border ${item.iconBg} transition-transform group-hover:scale-105 shadow-2xs`}
                                        >
                                            <Icon className="w-4 h-4" />
                                        </div>
                                        <div className="min-w-0">
                                            <div className="flex items-center gap-1.5">
                                                <p className="text-xs font-bold text-slate-900 truncate">
                                                    {item.title}
                                                </p>
                                            </div>
                                            <p className="text-[10px] text-slate-500 truncate mt-0.5">
                                                {item.subtitle}
                                            </p>
                                        </div>
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* Bottom Section: Quick Actions & Footer */}
                <div
                    className="p-3 border-t border-slate-100 bg-slate-50/60 space-y-2.5"
                    style={{
                        paddingBottom: "max(0.75rem, calc(env(safe-area-inset-bottom, 0px) + 0.75rem))",
                    }}
                >
                    {isCheckedIn && onCheckOutClick && (
                        <button
                            type="button"
                            onClick={() => {
                                if (window.innerWidth < 1024) setIsOpen(false);
                                onCheckOutClick();
                            }}
                            className="w-full py-2.5 px-3 rounded-xl bg-rose-50 hover:bg-rose-100 text-rose-700 border border-rose-200 text-xs font-bold transition-all flex items-center justify-center gap-2 cursor-pointer shadow-2xs active:scale-98"
                        >
                            <ArrowRightOnRectangleIcon className="w-4 h-4 text-rose-600" />
                            <span>Check Out of Site</span>
                        </button>
                    )}

                    <div className="w-full flex items-center justify-center text-[11px] border-t border-slate-200 text-slate-400 font-medium px-1">
                            <img
                                src={companyLogo}
                                alt="logo"
                                className="h-9 w-100 object-contain pt-2"
                                onError={(e) => {
                                    e.target.src = sunnyHomesCrmLogo;
                                }}
                            />
                       
                    </div>
                </div>
            </aside>
        </div>
    );
}
